<template>
	<div class="mg-t-20 mg-b-20 mg-lg-b-25 mg-xl-b-30">
		<loader-component v-if="loading" :loader-message="'Please wait...'"></loader-component>
		<div v-else>
			<div v-if="submitted" class="row justify-content-center">
				<div class="col-md-5">
					<request-summary-component :request="withdrawal" :type="'withdrawal'" :busy="cancelForm.busy"
						:request-cancelled="requestCancelled"></request-summary-component>

					<div class="col-12 mg-t-20 text-center">
						<countdown-button v-if="!requestCancelled" :initial-seconds="10"
							@countdown-finished="() => showGoBack = true" @cancel="cancelRequest">
							Cancel Request
						</countdown-button>
						<router-link v-if="showGoBack || requestCancelled"
							class="btn btn-default wd-lg-200 mx-auto shadow-sm" :to="{ name: 'dashboard' }">
							Go Back
						</router-link>
					</div>
				</div>
			</div>
			<div v-else class="row justify-content-center">
				<div class="col-lg-4">
					<div v-if="!canWithdraw" class="alert alert-warning shadow-sm" role="alert">
						You do not have any bank accounts saved.
						<router-link :to="{ name: 'messages.new', query: { topic: 'Account Update', message: '' } }"
							class="alert-link tx-orange">Click here</router-link> to request an update.
					</div>
					<form class="card shadow-sm" @submit.prevent="submitRequest">
						<div class="card-header pb-0 bd-b-0">
							<h5 class="card-title">Withdraw</h5>
							<h6 class="card-subtitle mb-2 text-muted">Please fill out the withdrawal form.</h6>
						</div>
						<div class="card-body">
							<div class="mg-b-25">
								<p class="tx-uppercase tx-11 tx-spacing-1 tx-color-03 tx-medium mg-b-5">Available Cash
									Balance</p>
								<h3 class="tx-26 tx-normal tx-rubik tx-spacing--2 mg-b-5">
									<small class="text-muted tx-light">{{ withdrawal.currency }}</small> {{
										withdrawal.currentBalance | toNumber }}
								</h3>
							</div>

							<div class="row">
								<div class="form-group col-lg-12 col-md-6">
									<label>Select Portfolio</label>
									<select v-model="form.external_portfolio_id"
										class="form-control custom-select shadow-sm"
										:class="{ 'is-invalid': form.errors.has('external_portfolio_id') }">
										<option selected :value="null" disabled>Select a portfolio</option>
										<option v-for="portfolioItem in portfolioOptions"
											:value="portfolioItem.external_portfolio_id" :key="portfolioItem.id">
											{{ portfolioItem.portfolio_type.name }}
										</option>
									</select>
									<has-error :form="form" field="external_portfolio_id"></has-error>
								</div>

								<div class="form-group col-lg-12 col-md-6">
									<label>Withdrawal Amount</label>
									<div class="input-group shadow-sm">
										<div class="input-group-prepend value">
											<span class="input-group-text">{{ form.currency | placeholder('') }}</span>
										</div>
										<input v-model="form.amount" :max="withdrawal.currentBalance" min="0"
											type="number" step="any" class="form-control"
											:class="{ 'is-invalid': form.errors.has('amount') }" placeholder="0.00"
											:readonly="!canWithdraw" />
									</div>
									<has-error :form="form" field="amount" :class="'d-block'"></has-error>
								</div>

								<div class="form-group col-lg-12 col-md-6">
									<label>Amount Remaining</label>
									<div class="input-group shadow-sm">
										<div class="input-group-prepend value">
											<span class="input-group-text">{{ form.currency | placeholder('') }}</span>
										</div>
										<input v-model="form.remaining_amount" type="number" step="any"
											class="form-control"
											:class="{ 'is-invalid': form.errors.has('remaining_amount') }"
											placeholder="0.00" readonly />
									</div>
									<has-error :form="form" field="remaining_amount" :class="'d-block'"></has-error>
								</div>

								<div class="form-group col-lg-12 col-md-6">
									<label>Settlement Account</label>
									<select v-model="form.settlement_account"
										class="form-control custom-select shadow-sm"
										:class="{ 'is-invalid': form.errors.has('settlement_account') }"
										:disabled="!canWithdraw">
										<option selected :value="null" disabled>Select an account</option>
										<option v-for="account in bankAccounts" :value="account.id" :key="account.id">{{
											`${account.bank_details.name} - ${account.account_number}` }}</option>
									</select>
									<has-error :form="form" field="settlement_account"></has-error>
								</div>
							</div>

							<div class="d-flex mg-t-15 mg-b-25">
								<button-component :busy="form.busy" :label="'Submit'" :label-loading="'Please wait...'"
									:button-class="'btn btn-default shadow-sm flex-fill'"></button-component>
								<router-link class="btn btn-primary shadow-sm flex-fill mg-l-10"
									:to="{ name: 'dashboard' }">Cancel</router-link>
							</div>
						</div>
					</form>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import { requests } from '../../../../mixins'

export default {
	mixins: [requests.requests],
	mounted() {
		this.fetchData();
	},
	data() {
		const { external_client_id } = this.$store.getters.company;
		const { id } = this.$store.getters.user.client;

		return {
			requestCancelled: false,
			showGoBack: false,
			loading: true,
			canWithdraw: false,
			submitted: false,
			hasSettlementAccount: true,
			bankAccounts: [],
			holding: {},
			portfolio: [],
			withdrawal: {
				currency: 'GHS',
				currentBalance: null,
				holding: null,
				amount: null,
				paymentAccount: null,
			},
			form: new Form({
				external_client_id,
				client: id,
				holding_id: null,
				total_units: null,
				units: null,
				amount: null,
				remaining_units: null,
				remaining_amount: null,
				currency: 'GHS',
				external_portfolio_id: null,
				external_custody_account_id: null,
				settlement_account: null,
				type: 'withdrawal',
				security: 'ICAMGH-F003',
			}),
			cancelForm: new Form({
				request: null,
			}),
		};
	},
	computed: {
		...mapGetters({
			user: 'user',
			company: 'company',
			cashBalance: 'trade/cashBalance',
		}),
		portfolioOptions() {
			return this.portfolio.filter((_portfolio) => !_.isEmpty(_portfolio.holdings))
		},
	},
	watch: {
		'form.settlement_account': function (value) {
			this.withdrawal.paymentAccount = this.bankAccounts.find((account) => account.id === value);
		},
		'form.amount': function (amount) {
			if (amount) {
				this.calculateWithdrawal(amount);
			} else {
				this.form.amount = this.form.units = this.form.remaining_units = this.form.remaining_amount = this.withdrawal.amount = null;
			}
		},
		'form.external_portfolio_id': function (value) {
			if (value) {
				const { currency, holdings, portfolio_type } = this.portfolio.find((portfolio) => portfolio.external_portfolio_id === value);

				this.form.currency = this.withdrawal.currency = currency;

				this.setHoldingByPortfolioType(holdings, portfolio_type);
			}
		},
	},
	methods: {
		calculateWithdrawal(value = 0) {
			if (value) {
				const remaining = parseFloat(this.withdrawal.currentBalance) - parseFloat(value);

				this.form.units = this.withdrawal.amount = parseFloat(value);
				this.form.remaining_amount = this.form.remaining_units = parseFloat(remaining).toFixed(2);
			} else {
				this.form.amount = this.form.units = this.form.remaining_units = this.form.remaining_amount = this.withdrawal.amount = null;
			}
		},
		setPortfolio(portfolio) {
			const exclude = ['ghs-income-portfolio', 'usd-income-portfolio', 'global-growth-portfolio'];

			portfolio.some((obj) => {
				if (_.kebabCase(obj.portfolio_type.name) === 'ghs-discretionary-portfolio') {
					obj.portfolio_type.name = 'GHS Cash';
					return true;
				}
			});

			return _.filter(portfolio, (obj) => !exclude.includes(slugify(obj.portfolio_type.name, { lower: true })));
		},
		setHoldingByPortfolioType(holdings, { name: portfolio_type_name, id: portfolio_type_id }) {
			let security;

			this.holdings = _.sortBy(holdings, [(obj) => obj.security_details.name]);

			switch (portfolio_type_id) {
				case 2:
					security = 'ICAMGH-F003';
					break;
				case 3:
					security = 'ICAMGH-F004';
					break;
				case 4:
				case 'usd-cash':
					security = 'ICAMGH-F002';
					break;
				case 5:
					security = 'ICAMGH-F001';
					break;
				default:
					security = 'ICAMGH-C001';
					break;
			}

			const holding = holdings.find((obj) => obj.security === security);
			const { id: holding_id } = holding;

			this.form.total_units = this.withdrawal.currentBalance = this.holdingValue(holding);
			this.withdrawal.holding = holding;
			this.form.holding_id = holding_id;
			this.form.external_custody_account_id = holding.external_custody_account_id;
		},
		getBankAccounts(external_client_id) {
			return axios.get(`/api/companies/${external_client_id}`, {
				params: {
					load: 'bank_account_information',
				},
			});
		},
		getUserPortfolios() {
			const { external_client_id } = this.company;

			return axios.get(`/api/companies/${external_client_id}/portfolios`, {
				params: {
					noAllocation: true,
				},
			});
		},
		fetchData() {
			const { external_client_id } = this.company;
			this.loading = true;

			axios
				.all([this.getBankAccounts(external_client_id), this.getUserPortfolios()])
				.then(
					axios.spread((bankAccountResponse, portfolioResponse) => {
						this.bankAccounts = bankAccountResponse.data.data.company.bank_account_information;
						const iclfPot = portfolioResponse.data.data.portfolio.find((pot) => pot.portfolio_type_id === 2);
						if (iclfPot) {
							this.form.external_portfolio_id = iclfPot.external_portfolio_id
						}
						this.portfolio = this.setPortfolio(portfolioResponse.data.data.portfolio);

						this.canWithdraw = !_.isEmpty(this.bankAccounts);
						this.form.external_client_id = external_client_id;
					})
				)
				.finally(() => (this.loading = false));
		},
		submitRequest() {
			this.form.post('/api/requests').then(({ data: { data } }) => {
				this.submitted = true;
				this.cancelForm.request = data.message.id
			});
		},
		cancelRequest() {
			this.cancelForm.post(`/api/requests/${this.cancelForm.request}/cancel`)
				.then(() => {
					console.log('request cancelled successfully.');
				})
				.catch(() => {
					console.log('request not cancelled successfully.');
				})
				.finally(() => this.requestCancelled = true)

		},
	},
};
</script>

<style></style>